import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import DropdownItem from './dropdown-item';
import { DropdownItemInterface } from '../../models/interfaces/dropdown-item.interface';
import { P2TextLabel } from 'react-bootstrap-common-script';
import useIsMobile from '../../hooks/use-is-mobile';
export interface DropdownProps extends React.HtmlHTMLAttributes<HTMLDivElement>{
  variant?: "large" | "small";
  disabled?: boolean,
  items:DropdownItemInterface[];
  selectedDropdown?: any;
  setSelectedDropdown: React.Dispatch<React.SetStateAction<any>>;
  placeholder?:string;
  label?:string;
  required?:boolean;
  hasTooltip?:boolean;
  allowAddNew?:boolean;
  allPlusIcon?: boolean;
  notApplicable?: boolean;
  onAddNewClick?:()=>void;
  onNotApplicableClick?:()=>void;
  addNewTitle?:string;
  tooltipContent?:any;
  forceError?: boolean;
}

const getIsDropdownItemActive = (currentSelectedValue: any, hoveredValue: any, itemValue: any) => {
  if(hoveredValue !== undefined){
      return hoveredValue === itemValue;
  }else{
    return currentSelectedValue === itemValue;
  }
}

const getBackgroundColor = (disabled: boolean, isExpand: boolean, selectedDropdown?: any) => {
  return (!disabled && !isExpand && selectedDropdown) ? "#F7F7F7" : "";
}

const getExpandedProps = (isExpand: boolean) => {
  return {
    iconActive: isExpand ? 'active' : '',
    dropdownRadius: isExpand ? 'no_bh_radius' : '',
    boxShadow: isExpand ? "0px 0px 2px 4px #64C8C880" : "",
    overflow: isExpand ? "unset" : "hidden"
  }
}

const getDisabledProps = (disabled:boolean) => {
  return {
    stroke: disabled ? "#D9D9D9" : "#0C8188",
    disabledClass: disabled ? 'polymer_dropdown-disabled' : ''
  }
}
const getTop = (height?: number, isMobile?: boolean) => {
  let top_height = 0;
  let additional_height = isMobile ? 0 : 1;
  if(height){
    top_height = Number(height) + additional_height;
  }
  return top_height;
}

const getPlatformStyle = (isMobile: boolean, isExpand: boolean) => {
  return {
    dropdownItemContainerMargin: isMobile ? "0" : "0 16px",
    dropdownMainSelectorBorderRadius: isMobile && isExpand? "8px 8px 0 0" : "8px"
  }
}


const getDropdownClass = (isValueSelected:boolean, disabled:boolean) => {
  if (isValueSelected && !disabled) {
    return 'polymer_dropdown-value'
  }

  return disabled ? 'polymer_dropdown-disabled' : 'polymer_dropdown-placeholder'
}
export interface DropdownHandle{
  isValid:()=>boolean
}
const Dropdown = forwardRef(({allowAddNew=false, allPlusIcon=false, notApplicable=false, disabled=false, required=false, hasTooltip=false, placeholder="Select a dropdown", variant="small", tooltipContent=undefined, forceError=false,...props}:DropdownProps,ref) => {

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [ isExpand, setExpand ] = useState<boolean>(false);
  const [ hoveredValue, setHoveredValue ] = useState<any>();
  const mainDropdownRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useIsMobile();

  useImperativeHandle(ref, ():DropdownHandle => {
    return {
      isValid() {
        return !!props.selectedDropdown;
      },
    };
  }, [props.selectedDropdown]);


  const onSelectDropdown = useCallback((e:DropdownItemInterface)=>{
    props.setSelectedDropdown(e?.value ?? '');
    setHoveredValue(undefined);
    setExpand(prev => !prev);
  },[props])

  const onAddNewSelect = useCallback(()=>{
    props.setSelectedDropdown('');
    props.onAddNewClick?.();
    setHoveredValue(undefined);
    setExpand(prev => !prev);
  },[props]);

  const onNotApplicableSelect = useCallback(()=>{
    props.setSelectedDropdown('Not applicable');
    props.onNotApplicableClick?.();
    setHoveredValue(undefined);
    setExpand(prev => !prev);
  },[props]);
  
  let defaultClass: string = "";
  switch(variant){
    case "small":
      defaultClass = "polymer_dropdown_container-small";
      break;
    case "large": 
    default: 
      defaultClass = "polymer_dropdown_container-large";
      break;
  }
 
  useEffect(() => {
    function handleClickOutside(event:any) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setHoveredValue(undefined);
        setExpand(false);
      }
    }
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.addEventListener('click', handleClickOutside);
    };
  }, [dropdownRef]);

  const handleSelectDropdown = useCallback((item:DropdownItemInterface) => () => {
    onSelectDropdown(item);
  },[onSelectDropdown]);

  const handleMouseEnter = useCallback((value: any) => () => {
    setHoveredValue(value);
  },[]);
  const isValueSelected = useMemo(()=>{
    return props.selectedDropdown || typeof props.selectedDropdown === 'boolean';
  },[props.selectedDropdown])

  const dropdownValue = useMemo(()=>{
    if( props.selectedDropdown === 'Not applicable'){
      return 'Not applicable';
    }
    else{
      return props.items.find(e=>e.value === props.selectedDropdown)?.title;
    }

  },[props.items, props.selectedDropdown])

  return (
    <>
    <div ref={dropdownRef} className='polymer_dropdown_wrapper' style={{...props.style}}>
      {props.label && <P2TextLabel bold={props.style?.fontWeight} marginBottom='10px' label={props.label} required={required} hasTooltip={hasTooltip} tooltipContent={tooltipContent}/>}
      <div aria-disabled={disabled} className={`polymer_dropdown ${getExpandedProps(isExpand).dropdownRadius} ${getDisabledProps(disabled).disabledClass}`} style={{overflow: getExpandedProps(isExpand).overflow,borderColor: !disabled && !isExpand && props?.selectedDropdown ? "#575757" : "", boxShadow: getExpandedProps(isExpand).boxShadow, borderRadius: getPlatformStyle(isMobile, isExpand).dropdownMainSelectorBorderRadius}}>
        <div className={`${defaultClass} d-flex`} ref={mainDropdownRef} onClick={()=>{
          if(!disabled){
            if(isExpand){
              setHoveredValue(undefined);
            }
            setExpand(prev => !prev);
          }
         }} style={{ backgroundColor: getBackgroundColor(disabled, isExpand, props.selectedDropdown), borderRadius: getPlatformStyle(isMobile, isExpand).dropdownMainSelectorBorderRadius}}>
          <div className='polymer_dropdown_selector-container flex-grow-1 align-self-center'>
            <p className={`p-0 m-0 ${getDropdownClass(isValueSelected, disabled)}`}>
              {isValueSelected && dropdownValue}
              {!isValueSelected && placeholder}
            </p>
          </div>
          <div className={`polymer_dropdown__icon ${getExpandedProps(isExpand).iconActive}`}>
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="11" viewBox="0 0 21 11" fill="none">
              <path d="M1.5 2L10.25 9L19 2"  stroke={getDisabledProps(disabled).stroke} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        {isExpand && <div className='dropdown_item_container' style={{ top: getTop(mainDropdownRef?.current?.offsetHeight, isMobile), margin: getPlatformStyle(isMobile, isExpand).dropdownItemContainerMargin}}>
          {
            props.items.map((e:DropdownItemInterface, _index:number)=>{
              if (e.isShown == false) {
                return;
              }
              
              const id = `${e.id}_${_index}`;
              return <DropdownItem key={id} onMouseEnter={handleMouseEnter(e.value)} onSelectDropdown={handleSelectDropdown(e)} isActive={getIsDropdownItemActive(props.selectedDropdown, hoveredValue, e.value)} title={e.title} allPlusIcon={allPlusIcon}/>
            })
          }
          {
            allowAddNew && <DropdownItem key={9999} onMouseEnter={handleMouseEnter("add_new")} isActive={getIsDropdownItemActive(props.selectedDropdown, hoveredValue, "add_new")} onSelectDropdown={onAddNewSelect} variant={"add_new"} title={props.addNewTitle}/>
          }
          {
            notApplicable && <DropdownItem key={99999} onSelectDropdown={onNotApplicableSelect} title="Not applicable" onMouseEnter={handleMouseEnter("not_applicable")}  isActive={getIsDropdownItemActive(props.selectedDropdown, hoveredValue, "not_applicable")}/>
          }
        </div>}
      </div>
    </div>
    { !props.selectedDropdown && props.selectedDropdown !== false && forceError && 
      <div style={{ marginTop: 10 }}>
        <p className='m-0 polymer-fields-error-message' style={{color:'#CB2A0C'}}>{"This is a required field."}</p>
      </div>
    }
    </>
  )
})

export default Dropdown